// components/slides/SlidesProposalStatus.tsx
'use client';

import { useSlidesStore } from './slidesStore';
import { proposalHasCompleteResult } from '#lib/slides/reviewStatus';
import { SlidesRunStatus } from './SlidesRunStatus';

const PRESENTATION = {
  running: { text: 'Counter-proposal in progress', detail: 'Preparing the counter-proposal.' },
  saving: { text: 'Saving counter-proposal', detail: 'Waiting for save confirmation.' },
  completed: { text: 'Counter-proposal completed', detail: 'Both sheets saved. Review them before sharing.' },
  stopped: { text: 'Counter-proposal stopped', detail: 'The workflow did not finish. Existing results are kept.' },
  failed: { text: 'Counter-proposal failed', detail: 'The workflow did not finish. Existing results are kept.' },
  incomplete: { text: 'Counter-proposal incomplete', detail: 'Both proposal sheets have not been completed.' },
  unsaved: { text: 'Counter-proposal not saved', detail: 'Your results are kept in this tab.' },
  unknown: { text: 'Counter-proposal status unavailable', detail: 'No active counter-proposal in this tab. You can start a new one.' },
} as const;

export function SlidesProposalStatus() {
  const deck = useSlidesStore(s => s.activeProjectId ? s.decks[s.activeProjectId] : undefined);
  const ownedRunId = useSlidesStore(s => s.activeProjectId ? s.runtime[s.activeProjectId]?.proposalRunId : undefined);
  const progress = useSlidesStore(s => s.activeProjectId ? s.runtime[s.activeProjectId]?.proposalProgress : undefined);
  const save = useSlidesStore(s => s.activeProjectId ? s.saveStates[s.activeProjectId] : undefined);
  const run = deck?.doc.proposalRun;
  if (!deck || !run || run.round !== deck.reviewRound) return null;
  let status: keyof typeof PRESENTATION = run.status;
  if (status === 'running') status = ownedRunId === run.id ? 'running' : 'unknown';
  if (status === 'completed' && !proposalHasCompleteResult(deck)) status = 'incomplete';
  const notSaved = (save?.savedVersion ?? -1) < deck.version;
  if (status === 'completed' && notSaved) status = save?.status === 'error' ? 'unsaved' : 'saving';
  if (!Object.hasOwn(PRESENTATION, status)) status = 'unknown';
  // A run marked running that no tab drives says nothing useful: no line (ChatGPT pass, 2026-10-06).
  if (status === 'unknown') return null;
  const { text } = PRESENTATION[status];
  let detail: string = PRESENTATION[status].detail;
  if (status === 'running') {
    if (progress?.phase === 'generating' || progress?.phase === 'checking') {
      detail = `${progress.phase === 'generating' ? 'Generating' : 'Checking'} sheet ${progress.sheet} of 2${progress.attempt ? ` · attempt ${progress.attempt}` : ''}.`;
    } else if (progress?.phase === 'assembling') detail = 'Assembling both sheets.';
    else if (progress?.phase === 'finishing') detail = 'Both sheets prepared. Finishing the workflow.';
  } else if (status === 'completed' && run.needsInspection) {
    detail = 'Both sheets saved. Drafts need inspection: at least one did not pass all checks.';
  }
  const saveError = notSaved && save?.status === 'error';
  const busy = status === 'running' || status === 'saving';
  const unsavedDetail = notSaved && !busy && status !== 'unsaved'
    ? saveError ? 'Changes are not saved. They are kept in this tab.' : 'Waiting for save confirmation.' : undefined;
  return <SlidesRunStatus status={status} text={text} detail={detail} unsavedDetail={unsavedDetail} showSaveRecovery={saveError} />;
}
